import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon } from '~/components';
import { colors, weights } from '~/styles';
import { JsonBlock } from './shared';
import { computeSpanCost, formatCost } from './formatUtils';

const Block = styled.div`
  border: solid 1px ${colors.grey10};
  border-radius: 0.25rem;
  margin: 0.5rem 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  font-size: 0.8125rem;

  &:hover {
    background: ${colors.grey5};
  }
`;

const Chevron = styled.span`
  font-size: 0.625rem;
  color: ${colors.grey40};
  width: 0.75rem;
`;

const StepLabel = styled.span`
  font-weight: ${weights.bold};
  color: ${colors.black};
`;

const Meta = styled.span`
  color: ${colors.grey50};
  font-size: 0.75rem;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.125rem 0.375rem;
  border-radius: 0.125rem;
  font-size: 0.6875rem;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  background: ${colors.grey5};
  color: ${colors.grey55};
`;

const Body = styled.div`
  padding: 0.75rem;
  border-top: solid 1px ${colors.grey10};
`;

const SectionHeader = styled.div`
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  color: ${colors.grey50};
  text-transform: uppercase;
  margin-top: 1rem;
  margin-bottom: 0.5rem;

  &:first-child {
    margin-top: 0;
  }
`;

const ToolResult = styled.div`
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: solid 1px ${colors.grey10};
  border-radius: 0.25rem;
`;

const ToolHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const ToolResultIcon = styled(Icon)`
  font-size: 0.625rem;
`;

const ToolNameLabel = styled.span`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  color: ${colors.black};
`;

const ErrorText = styled.div`
  color: ${colors.danger};
  font-size: 0.8125rem;
  margin-top: 0.25rem;
`;

const UsageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const UsageItem = styled.div`
  font-size: 0.75rem;
  color: ${colors.grey55};
`;

const UsageValue = styled.span`
  font-weight: ${weights.bold};
  color: ${colors.black};
`;

const JsonLabel = styled.div`
  font-size: 0.75rem;
  color: ${colors.grey50};
  margin-bottom: 0.25rem;
`;

const ToolListBlock = styled.div`
  margin-bottom: 0.5rem;
`;

const ToolListNames = styled.div`
  font-size: 0.75rem;
  font-family: monospace;
  color: ${colors.grey55};
`;

const RecoveryItem = styled.div`
  font-size: 0.8125rem;
  color: ${colors.grey55};
  margin-bottom: 0.25rem;
`;

const ExpandToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: ${colors.primary};
  cursor: pointer;
  height: auto;
  border-radius: 0;
  font-weight: ${weights.normal};

  &:hover {
    color: ${colors.accent};
    background: none;
  }
`;

function formatTokens(n) {
  if (n == null) return '-';
  return n.toLocaleString();
}

function formatDuration(ms) {
  if (ms == null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function CollapsibleJson({ data, label }) {
  const [expanded, setExpanded] = useState(false);

  if (!data) return null;

  const preview = JSON.stringify(data).slice(0, 200);
  const full = JSON.stringify(data, null, 2);
  const isLong = full.length > 200;

  return (
    <div>
      {label && <JsonLabel>{label}</JsonLabel>}
      <JsonBlock>{expanded ? full : preview + (isLong ? '...' : '')}</JsonBlock>
      {isLong && <ExpandToggle onClick={() => setExpanded(!expanded)}>{expanded ? 'Collapse' : 'Expand'}</ExpandToggle>}
    </div>
  );
}

export default function SpanBlock({ span, index, pricing }) {
  const [expanded, setExpanded] = useState(false);

  const model = span.request?.eventData?.model || span.response?.eventData?.model;
  const usage = span.response?.eventData?.usage;
  const inputTokens = usage?.input_tokens;
  const outputTokens = usage?.output_tokens;
  const cost = computeSpanCost(usage, pricing?.[model]);

  return (
    <Block>
      <Header onClick={() => setExpanded(!expanded)}>
        <Chevron>
          <Icon icon={expanded ? 'chevron-down' : 'chevron-right'} />
        </Chevron>
        <StepLabel>{span.label || `Step ${index + 1}`}</StepLabel>
        {model && <Badge>{model}</Badge>}
        {inputTokens != null && (
          <Meta>
            {formatTokens(inputTokens)} in / {formatTokens(outputTokens)} out
          </Meta>
        )}
        {cost && <Meta>{formatCost(cost.totalCost)}</Meta>}
        <Meta>{formatDuration(span.durationMs)}</Meta>
      </Header>

      {expanded && (
        <Body>
          {span.request && (
            <>
              <SectionHeader>Request</SectionHeader>
              {span.request.eventData?.tools && (
                <ToolListBlock>
                  <JsonLabel>Tools ({span.request.eventData.tools.length})</JsonLabel>
                  <ToolListNames>
                    {span.request.eventData.tools
                      .map((t) => t.name || t.function?.name)
                      .filter(Boolean)
                      .join(', ')}
                  </ToolListNames>
                </ToolListBlock>
              )}
              <CollapsibleJson data={span.request.eventData?.input} label="Input" />
            </>
          )}

          {span.response && (
            <>
              <SectionHeader>Response</SectionHeader>
              {usage && (
                <UsageGrid>
                  <UsageItem>
                    Input: <UsageValue>{formatTokens(usage.input_tokens)}</UsageValue>
                  </UsageItem>
                  <UsageItem>
                    Output: <UsageValue>{formatTokens(usage.output_tokens)}</UsageValue>
                  </UsageItem>
                  {usage.input_tokens_details?.cached_tokens != null && (
                    <UsageItem>
                      Cached: <UsageValue>{formatTokens(usage.input_tokens_details.cached_tokens)}</UsageValue>
                    </UsageItem>
                  )}
                  {cost && (
                    <>
                      <UsageItem>
                        Input cost: <UsageValue>{formatCost(cost.inputCost)}</UsageValue>
                      </UsageItem>
                      {cost.cachedInputCost > 0 && (
                        <UsageItem>
                          Cached cost: <UsageValue>{formatCost(cost.cachedInputCost)}</UsageValue>
                        </UsageItem>
                      )}
                      <UsageItem>
                        Output cost: <UsageValue>{formatCost(cost.outputCost)}</UsageValue>
                      </UsageItem>
                      <UsageItem>
                        Total cost: <UsageValue>{formatCost(cost.totalCost)}</UsageValue>
                      </UsageItem>
                    </>
                  )}
                </UsageGrid>
              )}
              <CollapsibleJson data={span.response.eventData?.output} label="Output" />
            </>
          )}

          {span.toolOutputs.length > 0 && (
            <>
              <SectionHeader>Tool Results</SectionHeader>
              {span.toolOutputs.map((t, i) => (
                <ToolResult key={t.eventData?.call_id || i}>
                  <ToolHeader>
                    <ToolResultIcon icon="check" color={colors.success} />
                    <ToolNameLabel>{t.eventData?.name}</ToolNameLabel>
                  </ToolHeader>
                  <CollapsibleJson data={t.eventData?.arguments} label="Arguments" />
                  <CollapsibleJson data={t.eventData?.result} label="Result" />
                </ToolResult>
              ))}
            </>
          )}

          {span.toolErrors.length > 0 && (
            <>
              <SectionHeader>Tool Errors</SectionHeader>
              {span.toolErrors.map((t, i) => (
                <ToolResult key={t.eventData?.call_id || i}>
                  <ToolHeader>
                    <ToolResultIcon icon="xmark" color={colors.danger} />
                    <ToolNameLabel>{t.eventData?.name}</ToolNameLabel>
                  </ToolHeader>
                  <CollapsibleJson data={t.eventData?.arguments} label="Arguments" />
                  <ErrorText>{t.eventData?.error}</ErrorText>
                </ToolResult>
              ))}
            </>
          )}

          {span.recoveries.length > 0 && (
            <>
              <SectionHeader>Recoveries</SectionHeader>
              {span.recoveries.map((r, i) => (
                <RecoveryItem key={i}>
                  Missing tool output recovery: {r.eventData?.callId} ({r.eventData?.method})
                </RecoveryItem>
              ))}
            </>
          )}
        </Body>
      )}
    </Block>
  );
}
